/**
 * Mounts one HangarHost per effect run, like `useGame` does for a match: React StrictMode runs
 * the effect twice in development, so the first instance is aborted and disposed once its
 * renderer init settles and nothing leaks.
 */
import type { Loadout } from '@steelwake/shared/loadout';
import { useEffect, useRef, useState, type RefObject } from 'react';

import { installHangarDebugApi, removeDebugApi } from '../../game/debugApi.js';
import { HangarHost, type HangarStatus } from '../../game/hangar/HangarHost.js';
import { applyUrlSettings, parseUrlOptions } from '../../game/urlParams.js';
import { useSettingsStore } from '../../settings/store.js';

export function useHangarScene(
  mount: RefObject<HTMLDivElement | null>,
  enabled: boolean,
  initial: { mech: string; loadout: Loadout | null },
): { hostRef: RefObject<HangarHost | null>; status: HangarStatus } {
  const [status, setStatus] = useState<HangarStatus>({ phase: 'probing' });
  const hostRef = useRef<HangarHost | null>(null);
  const initialRef = useRef(initial);
  initialRef.current = initial;

  useEffect(() => {
    const container = mount.current;
    if (!enabled || !container) return;
    const url = parseUrlOptions(window.location.search);
    applyUrlSettings(useSettingsStore.getState(), url.settings);

    const controller = new AbortController();
    const canvas = document.createElement('canvas');
    canvas.style.display = 'block';
    container.append(canvas);

    const host = new HangarHost({
      canvas,
      container,
      signal: controller.signal,
      settings: useSettingsStore.getState().settings,
      url,
      mech: initialRef.current.mech,
      loadout: initialRef.current.loadout,
      onStatus: (s) => {
        if (!controller.signal.aborted) setStatus(s);
      },
    });
    hostRef.current = host;
    installHangarDebugApi(host);

    const started = host.start().catch((err: unknown) => {
      if (controller.signal.aborted) return;
      const message = err instanceof Error ? err.message : String(err);
      host.fail(message);
      setStatus({ phase: 'error', message });
    });
    const unsubscribe = useSettingsStore.subscribe((state) => host.applySettings(state.settings));

    return () => {
      controller.abort();
      host.stop();
      unsubscribe();
      removeDebugApi(host);
      hostRef.current = null;
      void started.finally(() => host.dispose()).finally(() => canvas.remove());
    };
  }, [mount, enabled]);

  return { hostRef, status };
}
